/**
 * Export Use Cases Factory
 *
 * Assembles export-related use cases with their dependencies.
 *
 * @module @reports/composition/factories/createExportUseCases
 */

import type { AxiosInstance } from 'axios';
import type { AuthService } from 'types/AuthService';
import type { ExportUseCases } from '../types';

// Infrastructure imports
import { createReportsHttpClient } from '@reports/infrastructure/api';
import { createReportApiMapper } from '@reports/infrastructure/mappers';
import { createReportsExportRepository } from '@reports/infrastructure/repositories';

// Use cases imports
import { exportRequestUseCase } from '@reports/application/use-cases';

/**
 * Creates all export use cases with injected dependencies.
 */
export const createExportUseCases = (httpClient: AxiosInstance, authService: AuthService): ExportUseCases => {
  // Create infrastructure components
  const reportsHttpClient = createReportsHttpClient({ httpClient, authService });
  const mapper = createReportApiMapper();

  // Create export repository that implements the export port
  const exportPort = createReportsExportRepository({
    httpClient: reportsHttpClient,
    mapper,
  });

  // Compose use cases
  return {
    exportReport: exportRequestUseCase(exportPort),
  };
};
